import { ChatRequestError, RequestTimeoutError } from "./request-errors";
import { StreamInterruptedError } from "./stream";

export const isRecoverableStreamError = (error: unknown): boolean =>
  error instanceof StreamInterruptedError || error instanceof RequestTimeoutError || error instanceof TypeError ||
  error instanceof ChatRequestError && error.code === "network_error";

/** Wait without polling while offline/backgrounded; waking resumes the same session. */
export function waitForStreamRecovery(signal: AbortSignal, delayMs: number, status: (state: "offline" | "recovering") => void, notBefore = 0): Promise<void> {
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener("online", wake); window.removeEventListener("offline", wake);
      document.removeEventListener("visibilitychange", wake); signal.removeEventListener("abort", abort);
    };
    const abort = () => { cleanup(); reject(new DOMException("Aborted", "AbortError")); };
    const schedule = (delay: number) => {
      clearTimeout(timer);
      status(navigator.onLine === false ? "offline" : "recovering");
      if (navigator.onLine === false || document.visibilityState === "hidden") return;
      timer = setTimeout(() => { cleanup(); resolve(); }, Math.max(delay, notBefore - Date.now()));
    };
    const wake = () => schedule(0);
    if (signal.aborted) { abort(); return; }
    window.addEventListener("online", wake); window.addEventListener("offline", wake);
    document.addEventListener("visibilitychange", wake); signal.addEventListener("abort", abort, { once: true });
    schedule(delayMs);
  });
}

export const waitForPageVisible = (signal: AbortSignal): Promise<void> => {
  if (signal.aborted) return Promise.reject(new DOMException("Aborted", "AbortError"));
  if (document.visibilityState !== "hidden") return Promise.resolve();

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      signal.removeEventListener("abort", handleAbort);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") return;
      cleanup();
      resolve();
    };
    const handleAbort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    signal.addEventListener("abort", handleAbort, { once: true });
  });
};
